import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { faLink } from '@fortawesome/pro-light-svg-icons/faLink';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { useSmartLinkAddress } from '../../music24/promote/releases/useSmartLinkAddress';
import { presaveCreateMessages } from './presaveCreateMessages';
import { PresaveLinkBox } from './PresaveLinkBox';
import { PresaveSongCard } from './PresaveSongCard';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './PresaveCreate.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HasPageProps {
  accountId: number;
  item: CatalogItem;
  onEditPage: () => void;
  onPickAnother: () => void;
}

/** The song has its pre-save page (design presave-create c3): share it, make no second one. */
const PresaveHasPageComponent: React.FC<HasPageProps> = ({ accountId, item, onEditPage, onPickAnother }) => {
  const intl = useIntl();
  const address = useSmartLinkAddress(accountId, item.smartLinkSlug);
  const isLive = item.smartLinkStatus === 'published';
  return (
    <div className={styles.body} data-testid="presave-has-page">
      <div className={styles.qa}>
        <div>
          <div className={styles.eyebrow}>{intl.formatMessage(presaveCreateMessages.eyebrow)}</div>
          <h1 className={styles.question}>
            {intl.formatMessage(presaveCreateMessages.hasPageQuestion, { title: item.title })}
          </h1>
          <p className={styles.sub}>{intl.formatMessage(presaveCreateMessages.hasPageSub)}</p>
        </div>
        {address !== null && isLive && <PresaveLinkBox shareUrl={`https://${address}`} shareTitle={item.title} />}
        <div className={styles.actions}>
          <span className={styles.actionsNote}>{intl.formatMessage(presaveCreateMessages.hasPageNote)}</span>
          <SMLButton outline omitCap onClick={onPickAnother}>
            {intl.formatMessage(presaveCreateMessages.pickAnother)}
          </SMLButton>
          {item.smartLinkId !== null && (
            <SMLButton color="primary" omitCap onClick={onEditPage} testID="presave-has-page-edit">
              {intl.formatMessage(presaveCreateMessages.nextEditButton)}
            </SMLButton>
          )}
        </div>
      </div>
      <PresaveSongCard item={item} pill="live" />
    </div>
  );
};

export const PresaveHasPage = TypedMemo(PresaveHasPageComponent);

interface ReleasedProps {
  item: CatalogItem;
  onSmartLink: () => void;
  onPickAnother: () => void;
}

/** The song is out (design presave-create c4): a SmartLink, not a pre-save. */
const PresaveReleasedComponent: React.FC<ReleasedProps> = ({ item, onSmartLink, onPickAnother }) => {
  const intl = useIntl();
  return (
    <div className={styles.body} data-testid="presave-released">
      <div className={styles.qa}>
        <div>
          <div className={styles.eyebrow}>{intl.formatMessage(presaveCreateMessages.eyebrow)}</div>
          <h1 className={styles.question}>
            {intl.formatMessage(presaveCreateMessages.outQuestion, { title: item.title })}
          </h1>
          <p className={styles.sub}>{intl.formatMessage(presaveCreateMessages.outSub)}</p>
        </div>
        <div className={styles.actions}>
          <span className={styles.actionsNote} />
          <SMLButton outline omitCap onClick={onPickAnother}>
            {intl.formatMessage(presaveCreateMessages.pickAnother)}
          </SMLButton>
          <SMLButton color="primary" omitCap leftIcon={faLink} onClick={onSmartLink}>
            {intl.formatMessage(presaveCreateMessages.pickOutLink)}
          </SMLButton>
        </div>
      </div>
      <PresaveSongCard item={item} pill="out" />
    </div>
  );
};

export const PresaveReleased = TypedMemo(PresaveReleasedComponent);

interface MissingProps {
  onPickSong: () => void;
}

/** The song of the address is not in the Catalog of this artist (design presave-create d3). */
const PresaveSongMissingComponent: React.FC<MissingProps> = ({ onPickSong }) => {
  const intl = useIntl();
  return (
    <div className={clsx(styles.body, styles.bodySingle)} data-testid="presave-missing">
      <div className={styles.qa}>
        <span className={styles.kboxBad}>
          <FontAwesomeIcon icon={faCircleExclamation} />
        </span>
        <div>
          <h1 className={clsx(styles.question, styles.questionFlush)}>
            {intl.formatMessage(presaveCreateMessages.missingQuestion)}
          </h1>
          <p className={styles.sub}>{intl.formatMessage(presaveCreateMessages.missingSub)}</p>
        </div>
        <div className={styles.actions}>
          <span className={styles.actionsNote} />
          <SMLButton color="primary" omitCap onClick={onPickSong}>
            {intl.formatMessage(presaveCreateMessages.pickSong)}
          </SMLButton>
        </div>
      </div>
    </div>
  );
};

export const PresaveSongMissing = TypedMemo(PresaveSongMissingComponent);
